import { useState, useEffect } from 'react';
import React from 'react';
import { CONFIG, HowItWorks, Loading, SpendChart, api, formatMoney, spendSourceColor } from './_barrel.js';


        function AdminPanel({ token, analyst }) {
            const [pending, setPending] = useState([]);
            const [admins, setAdmins] = useState([]);
            const [tenants, setTenants] = useState([]);
            const [loading, setLoading] = useState(true);
            const [error, setError] = useState(null);
            const [inviteOpen, setInviteOpen] = useState(false);
            const [inviteForm, setInviteForm] = useState({ email: '', full_name: '' });
            const [inviteBusy, setInviteBusy] = useState(false);
            const [inviteMsg, setInviteMsg] = useState(null);
            // Expand a tenant to see / delete its client user accounts
            const [expandedTenant, setExpandedTenant] = useState(null);
            const [tenantAnalysts, setTenantAnalysts] = useState([]);
            const [tenantAnalystsBusy, setTenantAnalystsBusy] = useState(false);
            const [tenantAnalystsErr, setTenantAnalystsErr] = useState(null);

            // Costs panel state
            const today = new Date();
            const thirtyDaysAgo = new Date(today.getTime() - 30 * 86400000);
            const [spendFromDate, setSpendFromDate] = useState(thirtyDaysAgo.toISOString().slice(0, 10));
            const [spendToDate, setSpendToDate] = useState(today.toISOString().slice(0, 10));
            const [spendGranularity, setSpendGranularity] = useState('day');
            const [spendData, setSpendData] = useState(null);
            const [spendLoading, setSpendLoading] = useState(false);
            const [spendError, setSpendError] = useState(null);
            const [spendCurrency, setSpendCurrency] = useState('GBP');

            // Material-change alerts master switch
            const [maState, setMaState] = useState(null);   // {enabled, updated_at, updated_by, source}
            const [maBusy, setMaBusy] = useState(false);
            const [maError, setMaError] = useState(null);

            const api = `${CONFIG.apiBase}/api/v1/admin`;
            const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

            const refresh = async () => {
                setLoading(true);
                setError(null);
                try {
                    const [pRes, aRes, tRes] = await Promise.all([
                        fetch(`${api}/signups`, { headers }),
                        fetch(`${api}/admins`, { headers }),
                        fetch(`${api}/tenants`, { headers }),
                    ]);
                    if (!pRes.ok) throw new Error(`Pending signups: HTTP ${pRes.status}`);
                    if (!aRes.ok) throw new Error(`Admins: HTTP ${aRes.status}`);
                    if (!tRes.ok) throw new Error(`Tenants: HTTP ${tRes.status}`);
                    const pData = await pRes.json();
                    const aData = await aRes.json();
                    const tData = await tRes.json();
                    setPending(pData.items || []);
                    setAdmins(aData.items || []);
                    setTenants(tData.items || []);
                } catch (e) {
                    setError(e.message);
                } finally {
                    setLoading(false);
                }
            };

            useEffect(() => { refresh(); }, []);

            const loadSpend = async () => {
                setSpendLoading(true);
                setSpendError(null);
                try {
                    const from_dt = new Date(spendFromDate + 'T00:00:00Z').toISOString();
                    const to_dt = new Date(spendToDate + 'T23:59:59Z').toISOString();
                    const url = `${api}/spend?from_date=${encodeURIComponent(from_dt)}&to_date=${encodeURIComponent(to_dt)}&granularity=${spendGranularity}`;
                    const res = await fetch(url, { headers });
                    if (!res.ok) throw new Error(`Costs: HTTP ${res.status}`);
                    const data = await res.json();
                    setSpendData(data);
                } catch (e) {
                    setSpendError(e.message);
                } finally {
                    setSpendLoading(false);
                }
            };

            useEffect(() => { loadSpend(); }, [spendFromDate, spendToDate, spendGranularity]);

            const loadMaterialAlerts = async () => {
                setMaError(null);
                try {
                    const res = await fetch(`${api}/settings/material-alerts`, { headers });
                    if (!res.ok) throw new Error(`Alerts setting: HTTP ${res.status}`);
                    setMaState(await res.json());
                } catch (e) {
                    setMaError(e.message);
                }
            };

            useEffect(() => { loadMaterialAlerts(); }, []);

            const toggleMaterialAlerts = async (next) => {
                const goingLive = next && !(maState && maState.enabled);
                if (goingLive && !confirm(
                    'Turn material-change alerts ON?\n\n' +
                    'From the next hourly run, clients WILL receive "⚠ Something changed" ' +
                    'emails when something material happens to a monitored subject or company. ' +
                    'Each change is emailed at most once.\n\nProceed?')) return;
                setMaBusy(true);
                setMaError(null);
                try {
                    const res = await fetch(`${api}/settings/material-alerts`, {
                        method: 'PUT', headers, body: JSON.stringify({ enabled: next }),
                    });
                    if (!res.ok) throw new Error(`Save: HTTP ${res.status}`);
                    setMaState(await res.json());
                } catch (e) {
                    setMaError(e.message);
                } finally {
                    setMaBusy(false);
                }
            };

            const approve = async (id) => {
                if (!confirm('Approve this signup? The user will be able to log in.')) return;
                const res = await fetch(`${api}/signups/${id}/approve`, { method: 'POST', headers });
                if (!res.ok) { alert(`Approve failed: HTTP ${res.status}`); return; }
                refresh();
            };

            const reject = async (id) => {
                if (!confirm('Reject this signup? The tenant will be marked rejected and cannot log in.')) return;
                const res = await fetch(`${api}/signups/${id}/reject`, { method: 'POST', headers });
                if (!res.ok) { alert(`Reject failed: HTTP ${res.status}`); return; }
                refresh();
            };

            const deactivate = async (id) => {
                if (!confirm('Deactivate this admin? They will no longer be able to log in.')) return;
                const res = await fetch(`${api}/admins/${id}/deactivate`, { method: 'POST', headers });
                if (!res.ok) { alert(`Deactivate failed: HTTP ${res.status}`); return; }
                refresh();
            };

            const suspendTenant = async (id, name) => {
                if (!confirm(`Suspend ${name}? All their users will be blocked from logging in.`)) return;
                const res = await fetch(`${api}/tenants/${id}/suspend`, { method: 'POST', headers });
                if (!res.ok) {
                    const body = await res.json().catch(() => ({}));
                    alert(`Suspend failed: ${body?.error?.message || res.status}`);
                    return;
                }
                refresh();
            };

            const reactivateTenant = async (id, name) => {
                if (!confirm(`Reactivate ${name}? Their users will be able to log in again.`)) return;
                const res = await fetch(`${api}/tenants/${id}/reactivate`, { method: 'POST', headers });
                if (!res.ok) {
                    const body = await res.json().catch(() => ({}));
                    alert(`Reactivate failed: ${body?.error?.message || res.status}`);
                    return;
                }
                refresh();
            };

            const loadTenantAnalysts = async (tenantId) => {
                setTenantAnalystsBusy(true);
                setTenantAnalystsErr(null);
                setTenantAnalysts([]);
                try {
                    const res = await fetch(`${api}/tenants/${tenantId}/analysts`, { headers });
                    if (!res.ok) throw new Error(`HTTP ${res.status}`);
                    const data = await res.json();
                    setTenantAnalysts(data.items || []);
                } catch (e) {
                    setTenantAnalystsErr(e.message);
                } finally {
                    setTenantAnalystsBusy(false);
                }
            };

            const toggleTenantUsers = (tenantId) => {
                if (expandedTenant === tenantId) {
                    setExpandedTenant(null);
                } else {
                    setExpandedTenant(tenantId);
                    loadTenantAnalysts(tenantId);
                }
            };

            const deleteAnalyst = async (acct, tenantName) => {
                // Strong, typed confirmation — this is irreversible.
                const typed = prompt(
                    `PERMANENTLY DELETE this account from ${tenantName}?\n\n` +
                    `Name:  ${acct.display_name}\n` +
                    `Email: ${acct.email}\n\n` +
                    `This removes the account and its login entirely. It cannot be undone.\n` +
                    `Type the email address to confirm:`
                );
                if (typed === null) return;
                if (typed.trim().toLowerCase() !== (acct.email || '').toLowerCase()) {
                    alert('Email did not match — nothing was deleted.');
                    return;
                }
                const res = await fetch(`${CONFIG.apiBase}/api/v1/admin/analysts/${acct.id}`, {
                    method: 'DELETE', headers,
                });
                const body = await res.json().catch(() => ({}));
                if (!res.ok) {
                    alert(`Delete failed: ${body?.error?.message || `HTTP ${res.status}`}`);
                    return;
                }
                alert(`Deleted ${acct.email}.\nLogin: ${body.auth0 || 'n/a'}`);
                loadTenantAnalysts(expandedTenant);
                refresh();
            };

            const invite = async (e) => {
                e.preventDefault();
                if (!inviteForm.email || !inviteForm.full_name) { setInviteMsg('Both fields required'); return; }
                setInviteBusy(true);
                setInviteMsg(null);
                try {
                    const res = await fetch(`${api}/admins/invite`, {
                        method: 'POST', headers, body: JSON.stringify(inviteForm),
                    });
                    if (!res.ok) {
                        const body = await res.json().catch(() => ({}));
                        throw new Error(body?.error?.message || `HTTP ${res.status}`);
                    }
                    const body = await res.json();
                    setInviteMsg(body.message || 'Invitation sent');
                    setInviteForm({ email: '', full_name: '' });
                    setTimeout(() => { setInviteOpen(false); setInviteMsg(null); refresh(); }, 1500);
                } catch (e) {
                    setInviteMsg(e.message);
                } finally {
                    setInviteBusy(false);
                }
            };

            return (
                <div style={{padding:'24px', maxWidth:'1100px', margin:'0 auto'}}>
                    <div style={{
                        fontSize:'11px', letterSpacing:'0.12em', textTransform:'uppercase',
                        color:'var(--gold)', marginBottom:'8px',
                    }}>Platform Administration</div>
                    <div className="font-display" style={{fontSize:'28px', color:'var(--text)', marginBottom:'24px'}}>
                        Admin
                    </div>

                    <HowItWorks id="admin" title="How the Admin console works"
                        intro={'Admin governs who can use the workspace, the client organisations on it, and what it costs. It is scoped to platform administrators.'}
                        steps={[
                            '<b>Pending signups</b> — approve or decline people requesting access; approved analysts can then sign in and sign off assessments.',
                            '<b>Tenants</b> — each client organisation. The <b>Org code</b> is the client\'s login code (click to copy); <b>copy login link</b> gives them a ready-made sign-in URL with the code pre-filled. <b>Suspend</b> blocks a client\'s logins; expand the <b>Users</b> count to see a client\'s accounts and <b>Delete</b> a stray/duplicate one (removes the account and its Auth0 login — accounts with audit history are kept and can only be deactivated).',
                            '<b>Administrators</b> — see who holds admin rights and invite new team members by email.',
                            '<b>Retention</b> — the review queue of archived subjects past their retention date (GDPR). It is report-only unless auto-erase is switched on for the deployment; put a subject on <b>legal hold</b> from its record to keep it beyond the retention period.',
                            '<b>Costs</b> — review model spend over a date range and granularity to keep an eye on usage.',
                        ]} />

                    {error && <div style={{color:'var(--red)', marginBottom:'16px'}}>{error}</div>}

                    {/* Pending Signups panel */}
                    <div style={{
                        background:'var(--zone-2)', border:'1px solid var(--rule-2)',
                        padding:'20px', marginBottom:'24px',
                    }}>
                        <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'12px'}}>
                            <div className="font-display" style={{
                                fontSize:'14px', letterSpacing:'0.1em', textTransform:'uppercase', color:'var(--gold)',
                            }}>
                                Pending Signups <span style={{color:'var(--text-muted)'}}>· {pending.length}</span>
                            </div>
                            <button onClick={refresh} className="btn-gold" style={{padding:'4px 12px', fontSize:'11px'}}>
                                Refresh
                            </button>
                        </div>
                        {loading ? (
                            <div style={{color:'var(--text-muted)'}}>Loading…</div>
                        ) : pending.length === 0 ? (
                            <div style={{color:'var(--text-muted)', fontSize:'13px', padding:'12px 0'}}>
                                No pending signups. New tenant requests will appear here.
                            </div>
                        ) : (
                            <table style={{width:'100%', fontSize:'13px', borderCollapse:'collapse'}}>
                                <thead>
                                    <tr style={{textAlign:'left', color:'var(--text-muted)', fontSize:'11px', letterSpacing:'0.12em', textTransform:'uppercase'}}>
                                        <th style={{padding:'8px 8px 8px 0'}}>Company</th>
                                        <th style={{padding:'8px'}}>Requester</th>
                                        <th style={{padding:'8px'}}>Email</th>
                                        <th style={{padding:'8px'}}>Submitted</th>
                                        <th style={{padding:'8px', textAlign:'right'}}>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {pending.map(p => (
                                        <tr key={p.id} style={{borderTop:'1px solid var(--rule)'}}>
                                            <td style={{padding:'10px 8px 10px 0', color:'var(--text)'}}>{p.company_name}</td>
                                            <td style={{padding:'10px 8px', color:'var(--text)'}}>{p.requester_name}</td>
                                            <td style={{padding:'10px 8px', color:'var(--cyan)'}}>{p.requester_email}</td>
                                            <td style={{padding:'10px 8px', color:'var(--text-muted)'}}>
                                                {p.created_at ? new Date(p.created_at).toLocaleString('en-GB') : ''}
                                            </td>
                                            <td style={{padding:'10px 0', textAlign:'right'}}>
                                                <button onClick={() => approve(p.id)} className="btn-gold" style={{padding:'4px 10px', fontSize:'11px', marginRight:'6px'}}>
                                                    Approve
                                                </button>
                                                <button onClick={() => reject(p.id)} className="btn-gold" style={{padding:'4px 10px', fontSize:'11px', background:'transparent', color:'var(--red)', border:'1px solid var(--red)'}}>
                                                    Reject
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>

                    {/* Tenants panel */}
                    <div style={{
                        background:'var(--zone-2)', border:'1px solid var(--rule-2)',
                        padding:'20px', marginBottom:'24px',
                    }}>
                        <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'12px'}}>
                            <div className="font-display" style={{
                                fontSize:'14px', letterSpacing:'0.1em', textTransform:'uppercase', color:'var(--gold)',
                            }}>
                                Tenants <span style={{color:'var(--text-muted)'}}>· {tenants.length}</span>
                            </div>
                        </div>
                        {tenants.length === 0 ? (
                            <div style={{color:'var(--text-muted)', fontSize:'13px', padding:'12px 0'}}>
                                No tenants yet.
                            </div>
                        ) : (
                            <table style={{width:'100%', fontSize:'13px', borderCollapse:'collapse'}}>
                                <thead>
                                    <tr style={{textAlign:'left', color:'var(--text-muted)', fontSize:'11px', letterSpacing:'0.12em', textTransform:'uppercase'}}>
                                        <th style={{padding:'8px 8px 8px 0'}}>Tenant</th>
                                        <th style={{padding:'8px'}}>Org code</th>
                                        <th style={{padding:'8px'}}>Contact</th>
                                        <th style={{padding:'8px'}}>Users</th>
                                        <th style={{padding:'8px'}}>Last seen</th>
                                        <th style={{padding:'8px'}}>Status</th>
                                        <th style={{padding:'8px'}}>Created</th>
                                        <th style={{padding:'8px', textAlign:'right'}}>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {tenants.map(t => {
                                        const isPlatform = t.slug === 'obsidian-arc';
                                        const statusColor = t.approval_status === 'approved' && t.is_active ? 'var(--green)'
                                            : t.approval_status === 'pending' ? 'var(--gold)'
                                            : t.approval_status === 'rejected' ? 'var(--red)'
                                            : 'var(--text-muted)';
                                        const statusText = t.approval_status === 'approved' && !t.is_active ? 'suspended'
                                            : t.approval_status;
                                        return (
                                          <React.Fragment key={t.id}>
                                            <tr style={{borderTop:'1px solid var(--rule)'}}>
                                                <td style={{padding:'10px 8px 10px 0', color:'var(--text)'}}>
                                                    {t.display_name}
                                                    {isPlatform && <span style={{color:'var(--gold)', fontSize:'10px', marginLeft:'6px'}}>(platform)</span>}
                                                </td>
                                                <td style={{padding:'10px 8px'}}>
                                                    <div
                                                        onClick={(e) => { try { navigator.clipboard.writeText(t.slug); const el = e.currentTarget; const o = el.textContent; el.textContent = 'code copied ✓'; setTimeout(() => { el.textContent = o; }, 1200); } catch (err) {} }}
                                                        title="This client's login organisation code — click to copy"
                                                        style={{fontFamily:'IBM Plex Mono, monospace', fontSize:'12px', color:'var(--gold)', cursor:'pointer', borderBottom:'1px dotted var(--rule-2)', display:'inline-block'}}>
                                                        {t.slug} ⧉
                                                    </div>
                                                    <div
                                                        onClick={(e) => { try { navigator.clipboard.writeText(`${window.location.origin}/?tenant=${t.slug}`); const el = e.currentTarget; const o = el.textContent; el.textContent = 'link copied ✓'; setTimeout(() => { el.textContent = o; }, 1200); } catch (err) {} }}
                                                        title={`Direct sign-in link (pre-fills the code): ${window.location.origin}/?tenant=${t.slug} — click to copy`}
                                                        style={{fontSize:'10px', color:'var(--cyan)', cursor:'pointer', marginTop:'3px'}}>
                                                        🔗 copy login link
                                                    </div>
                                                </td>
                                                <td style={{padding:'10px 8px', color:'var(--cyan)', fontSize:'12px'}}>{t.intake_email || '—'}</td>
                                                <td style={{padding:'10px 8px'}}>
                                                    <span
                                                        onClick={() => toggleTenantUsers(t.id)}
                                                        title="View / manage this client's user accounts"
                                                        style={{color:'var(--cyan)', cursor:'pointer', borderBottom:'1px dotted var(--rule-2)'}}>
                                                        {t.analyst_count} {expandedTenant === t.id ? '▾' : '▸'}
                                                    </span>
                                                </td>
                                                <td style={{padding:'10px 8px', color: t.last_seen_at ? 'var(--text)' : 'var(--text-muted)', fontSize:'12px'}} title={t.last_seen_at || ''}>
                                                    {t.last_seen_label || 'Never'}
                                                </td>
                                                <td style={{padding:'10px 8px', color: statusColor}}>{statusText}</td>
                                                <td style={{padding:'10px 8px', color:'var(--text-muted)', fontSize:'12px'}}>
                                                    {t.created_at ? new Date(t.created_at).toLocaleDateString('en-GB') : ''}
                                                </td>
                                                <td style={{padding:'10px 0', textAlign:'right'}}>
                                                    {!isPlatform && t.approval_status === 'approved' && t.is_active && (
                                                        <button onClick={() => suspendTenant(t.id, t.display_name)} className="btn-gold" style={{padding:'4px 10px', fontSize:'11px', background:'transparent', color:'var(--red)', border:'1px solid var(--red)'}}>
                                                            Suspend
                                                        </button>
                                                    )}
                                                    {!isPlatform && t.approval_status === 'approved' && !t.is_active && (
                                                        <button onClick={() => reactivateTenant(t.id, t.display_name)} className="btn-gold" style={{padding:'4px 10px', fontSize:'11px'}}>
                                                            Reactivate
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                            {expandedTenant === t.id && (
                                                <tr style={{background:'var(--zone-1)'}}>
                                                    <td colSpan={8} style={{padding:'0 8px 14px 0'}}>
                                                        <div style={{border:'1px solid var(--rule-2)', borderTop:'none', padding:'12px 14px'}}>
                                                            <div style={{fontSize:'11px', letterSpacing:'0.1em', textTransform:'uppercase', color:'var(--text-muted)', marginBottom:'8px'}}>
                                                                User accounts · {t.display_name}
                                                            </div>
                                                            {tenantAnalystsBusy ? (
                                                                <div style={{color:'var(--text-muted)', fontSize:'12px'}}>Loading…</div>
                                                            ) : tenantAnalystsErr ? (
                                                                <div style={{color:'var(--red)', fontSize:'12px'}}>Failed to load: {tenantAnalystsErr}</div>
                                                            ) : tenantAnalysts.length === 0 ? (
                                                                <div style={{color:'var(--text-muted)', fontSize:'12px'}}>No user accounts.</div>
                                                            ) : (
                                                                <table style={{width:'100%', fontSize:'12px', borderCollapse:'collapse'}}>
                                                                    <tbody>
                                                                        {tenantAnalysts.map(acct => (
                                                                            <tr key={acct.id} style={{borderTop:'1px solid var(--rule)'}}>
                                                                                <td style={{padding:'7px 8px 7px 0', color:'var(--text)'}}>
                                                                                    {acct.display_name}
                                                                                    {acct.is_system && <span style={{color:'var(--text-muted)', fontSize:'10px', marginLeft:'6px'}}>(system)</span>}
                                                                                    {acct.id === analyst.id && <span style={{color:'var(--gold)', fontSize:'10px', marginLeft:'6px'}}>(you)</span>}
                                                                                </td>
                                                                                <td style={{padding:'7px 8px', color:'var(--cyan)'}}>{acct.email}</td>
                                                                                <td style={{padding:'7px 8px', color:'var(--text-muted)'}}>{acct.role}</td>
                                                                                <td style={{padding:'7px 8px', color: acct.is_active ? 'var(--green)' : 'var(--text-muted)'}}>
                                                                                    {acct.is_active ? 'active' : 'inactive'}
                                                                                </td>
                                                                                <td style={{padding:'7px 8px', color:'var(--text-muted)', fontSize:'11px'}}>
                                                                                    {acct.has_activity ? 'has history' : 'no history'}
                                                                                </td>
                                                                                <td style={{padding:'7px 0', textAlign:'right'}}>
                                                                                    {acct.is_system || acct.id === analyst.id ? null : acct.has_activity ? (
                                                                                        <span title="Accounts with audit history can't be hard-deleted — deactivate to block login while preserving the record"
                                                                                            style={{color:'var(--text-muted)', fontSize:'11px'}}>
                                                                                            deactivate only
                                                                                        </span>
                                                                                    ) : (
                                                                                        <button onClick={() => deleteAnalyst(acct, t.display_name)} className="btn-gold"
                                                                                            style={{padding:'3px 10px', fontSize:'11px', background:'transparent', color:'var(--red)', border:'1px solid var(--red)'}}>
                                                                                            Delete
                                                                                        </button>
                                                                                    )}
                                                                                </td>
                                                                            </tr>
                                                                        ))}
                                                                    </tbody>
                                                                </table>
                                                            )}
                                                            <div style={{color:'var(--text-muted)', fontSize:'10.5px', marginTop:'8px', lineHeight:1.5}}>
                                                                Delete permanently removes a duplicate/unused account and its login.
                                                                Accounts that have acted are kept for the audit trail — deactivate those from the client's own admin, or suspend the whole tenant above.
                                                            </div>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                          </React.Fragment>
                                        );
                                    })}
                                </tbody>
                            </table>
                        )}
                    </div>

                    {/* Material-change alerts panel */}
                    <div style={{background:'var(--zone-2)', border:'1px solid var(--rule-2)', padding:'20px', marginBottom:'24px'}}>
                        <div style={{display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:'16px', flexWrap:'wrap'}}>
                            <div style={{flex:'1 1 320px', minWidth:0}}>
                                <div className="font-display" style={{
                                    fontSize:'14px', letterSpacing:'0.1em', textTransform:'uppercase', color:'var(--gold)', marginBottom:'6px',
                                }}>
                                    "Something Changed" Alerts
                                </div>
                                <div style={{fontSize:'13px', color:'var(--text-muted)', lineHeight:1.5, maxWidth:'620px'}}>
                                    The round-the-clock watchdog. When ON, clients are emailed the moment something
                                    material happens to a monitored subject or company — a new sanctions or watchlist
                                    match, a fresh data breach, a new hostile threat, a jump in risk level, a new
                                    company owner, or a newly uncovered corporate link. Each change is emailed at most
                                    once. When OFF it runs as a <b>dry run</b>: it still checks hourly and logs what it
                                    <i> would</i> send, but sends nothing.
                                </div>
                                {maState && maState.source === 'db' && maState.updated_by && (
                                    <div style={{fontSize:'11px', color:'var(--text-muted)', marginTop:'8px'}}>
                                        Last changed by {maState.updated_by}
                                        {maState.updated_at ? ` · ${new Date(maState.updated_at).toLocaleString()}` : ''}
                                    </div>
                                )}
                                {maError && <div style={{color:'var(--red)', fontSize:'12px', marginTop:'8px'}}>{maError}</div>}
                            </div>
                            <div style={{flex:'0 0 auto', textAlign:'center'}}>
                                {maState === null ? (
                                    <div style={{color:'var(--text-muted)', fontSize:'12px', padding:'12px'}}>Loading…</div>
                                ) : (
                                    <>
                                        <div style={{
                                            fontSize:'11px', letterSpacing:'0.14em', textTransform:'uppercase', marginBottom:'8px',
                                            color: maState.enabled ? 'var(--green)' : 'var(--text-muted)',
                                        }}>
                                            {maState.enabled ? '● Live — sending' : '○ Off — dry run'}
                                        </div>
                                        <button
                                            onClick={() => toggleMaterialAlerts(!maState.enabled)}
                                            disabled={maBusy}
                                            title={maState.enabled ? 'Click to turn alerts OFF' : 'Click to turn alerts ON'}
                                            style={{
                                                position:'relative', width:'104px', height:'40px', borderRadius:'20px',
                                                border:'1px solid var(--rule-2)', cursor: maBusy ? 'wait' : 'pointer',
                                                background: maState.enabled ? 'var(--green)' : 'var(--zone-3)',
                                                transition:'background 0.15s ease', opacity: maBusy ? 0.6 : 1,
                                            }}>
                                            <span style={{
                                                position:'absolute', top:'3px', left: maState.enabled ? '67px' : '3px',
                                                width:'32px', height:'32px', borderRadius:'50%', background:'var(--text)',
                                                transition:'left 0.15s ease',
                                            }} />
                                            <span style={{
                                                position:'absolute', top:'50%', transform:'translateY(-50%)',
                                                left: maState.enabled ? '12px' : 'auto', right: maState.enabled ? 'auto' : '12px',
                                                fontSize:'10px', fontWeight:600, letterSpacing:'0.1em',
                                                color: maState.enabled ? 'var(--zone-1)' : 'var(--text-muted)',
                                            }}>{maState.enabled ? 'ON' : 'OFF'}</span>
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Admins panel */}
                    <div style={{background:'var(--zone-2)', border:'1px solid var(--rule-2)', padding:'20px'}}>
                        <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'12px'}}>
                            <div className="font-display" style={{
                                fontSize:'14px', letterSpacing:'0.1em', textTransform:'uppercase', color:'var(--gold)',
                            }}>
                                Admins <span style={{color:'var(--text-muted)'}}>· {admins.length}</span>
                            </div>
                            <button onClick={() => setInviteOpen(true)} className="btn-gold" style={{padding:'4px 12px', fontSize:'11px'}}>
                                + Invite Admin
                            </button>
                        </div>
                        {admins.length === 0 ? (
                            <div style={{color:'var(--text-muted)', fontSize:'13px', padding:'12px 0'}}>
                                No admins yet.
                            </div>
                        ) : (
                            <table style={{width:'100%', fontSize:'13px', borderCollapse:'collapse'}}>
                                <thead>
                                    <tr style={{textAlign:'left', color:'var(--text-muted)', fontSize:'11px', letterSpacing:'0.12em', textTransform:'uppercase'}}>
                                        <th style={{padding:'8px 8px 8px 0'}}>Name</th>
                                        <th style={{padding:'8px'}}>Email</th>
                                        <th style={{padding:'8px'}}>Role</th>
                                        <th style={{padding:'8px'}}>Status</th>
                                        <th style={{padding:'8px', textAlign:'right'}}>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {admins.map(a => (
                                        <tr key={a.id} style={{borderTop:'1px solid var(--rule)'}}>
                                            <td style={{padding:'10px 8px 10px 0', color:'var(--text)'}}>
                                                {a.display_name}
                                                {a.id === analyst.id && <span style={{color:'var(--gold)', fontSize:'10px', marginLeft:'6px'}}>(you)</span>}
                                            </td>
                                            <td style={{padding:'10px 8px', color:'var(--cyan)'}}>{a.email}</td>
                                            <td style={{padding:'10px 8px', color:'var(--text)'}}>{a.role}</td>
                                            <td style={{padding:'10px 8px', color: a.is_active ? 'var(--green)' : 'var(--text-muted)'}}>
                                                {a.is_active ? 'active' : 'inactive'}
                                            </td>
                                            <td style={{padding:'10px 0', textAlign:'right'}}>
                                                {a.id !== analyst.id && a.is_active && (
                                                    <button onClick={() => deactivate(a.id)} className="btn-gold" style={{padding:'4px 10px', fontSize:'11px', background:'transparent', color:'var(--text-muted)', border:'1px solid var(--rule-2)'}}>
                                                        Deactivate
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>

                    {/* Costs panel */}
                    <div style={{
                        background:'var(--zone-2)', border:'1px solid var(--rule-2)',
                        padding:'20px', marginBottom:'24px',
                    }}>
                        <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'12px'}}>
                            <div className="font-display" style={{
                                fontSize:'14px', letterSpacing:'0.1em', textTransform:'uppercase', color:'var(--gold)',
                            }}>
                                Costs
                                {spendData && (
                                    <span style={{color:'var(--text-muted)', marginLeft:'10px'}}>
                                        · {formatMoney(spendData.grand_total_usd, spendCurrency, 2)} {spendCurrency}
                                    </span>
                                )}
                            </div>
                            <div style={{display:'flex', gap:'8px', alignItems:'center', fontSize:'11px'}}>
                                <label style={{color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.12em'}}>From</label>
                                <input type="date" value={spendFromDate} onChange={e => setSpendFromDate(e.target.value)}
                                    style={{background:'var(--zone-3)', color:'var(--text)', border:'1px solid var(--rule-2)', padding:'4px 8px', fontSize:'11px'}} />
                                <label style={{color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.12em'}}>To</label>
                                <input type="date" value={spendToDate} onChange={e => setSpendToDate(e.target.value)}
                                    style={{background:'var(--zone-3)', color:'var(--text)', border:'1px solid var(--rule-2)', padding:'4px 8px', fontSize:'11px'}} />
                                <select value={spendCurrency} onChange={e => setSpendCurrency(e.target.value)}
                                    style={{background:'var(--zone-3)', color:'var(--text)', border:'1px solid var(--rule-2)', padding:'4px 8px', fontSize:'11px'}}>
                                    <option value="GBP">GBP £</option>
                                    <option value="USD">USD $</option>
                                </select>
                                <select value={spendGranularity} onChange={e => setSpendGranularity(e.target.value)}
                                    style={{background:'var(--zone-3)', color:'var(--text)', border:'1px solid var(--rule-2)', padding:'4px 8px', fontSize:'11px'}}>
                                    <option value="hour">Hour</option>
                                    <option value="day">Day</option>
                                    <option value="week">Week</option>
                                    <option value="month">Month</option>
                                </select>
                                <button onClick={loadSpend} className="btn-gold" style={{padding:'4px 12px', fontSize:'11px'}}>
                                    Refresh
                                </button>
                            </div>
                        </div>

                        {spendError && <div style={{color:'var(--red)', fontSize:'13px', marginBottom:'12px'}}>{spendError}</div>}

                        {spendLoading ? (
                            <div style={{color:'var(--text-muted)', padding:'24px 0', textAlign:'center'}}>Loading…</div>
                        ) : !spendData || spendData.buckets.length === 0 ? (
                            <div style={{color:'var(--text-muted)', fontSize:'13px', padding:'12px 0'}}>
                                No spend recorded in this range. Spend tracking is recent — data accumulates as the platform runs.
                            </div>
                        ) : (
                            <>
                                {/* Stacked bar chart */}
                                <SpendChart data={spendData} currency={spendCurrency} />

                                {/* Two-column totals */}
                                <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:'24px', marginTop:'20px'}}>
                                    {/* By source */}
                                    <div>
                                        <div style={{fontSize:'11px', letterSpacing:'0.09em', textTransform:'uppercase', color:'var(--text-muted)', marginBottom:'8px'}}>By Source</div>
                                        <table style={{width:'100%', fontSize:'12px', borderCollapse:'collapse'}}>
                                            <tbody>
                                                {spendData.totals_by_source.map(s => (
                                                    <tr key={s.source} style={{borderTop:'1px solid var(--rule)'}}>
                                                        <td style={{padding:'6px 0', color:'var(--text)'}}>
                                                            <span style={{display:'inline-block', width:'10px', height:'10px', background:spendSourceColor(s.source), marginRight:'8px', verticalAlign:'middle'}}></span>
                                                            {s.source}
                                                            {s.estimated && (
                                                                <span title="Estimated: upper-bound units × the configured per-unit rate, not a vendor-authoritative figure. Check the vendor's own billing for actual spend."
                                                                      style={{marginLeft:'6px', fontSize:'9px', letterSpacing:'0.08em', textTransform:'uppercase', color:'var(--amber)', border:'1px solid var(--amber)', borderRadius:'3px', padding:'0 4px', verticalAlign:'middle'}}>est.</span>
                                                            )}
                                                        </td>
                                                        <td style={{padding:'6px 0', color:'var(--text-muted)', textAlign:'right'}}>{s.units.toLocaleString('en-GB')} units</td>
                                                        <td style={{padding:'6px 0', color:'var(--gold)', textAlign:'right', fontFamily:'monospace'}}>{formatMoney(s.total_usd, spendCurrency, 4)}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>

                                    {/* By tenant */}
                                    <div>
                                        <div style={{fontSize:'11px', letterSpacing:'0.09em', textTransform:'uppercase', color:'var(--text-muted)', marginBottom:'8px'}}>By Tenant</div>
                                        {spendData.totals_by_tenant.length === 0 ? (
                                            <div style={{color:'var(--text-muted)', fontSize:'12px'}}>No tenant data.</div>
                                        ) : (
                                            <table style={{width:'100%', fontSize:'12px', borderCollapse:'collapse'}}>
                                                <tbody>
                                                    {spendData.totals_by_tenant.map(t => (
                                                        <tr key={t.tenant_id} style={{borderTop:'1px solid var(--rule)'}}>
                                                            <td style={{padding:'6px 0', color:'var(--text)'}}>{t.tenant_name}</td>
                                                            <td style={{padding:'6px 0', color:'var(--text-muted)', textAlign:'right'}}>{t.tenant_slug}</td>
                                                            <td style={{padding:'6px 0', color:'var(--gold)', textAlign:'right', fontFamily:'monospace'}}>{formatMoney(t.total_usd, spendCurrency, 4)}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        )}
                                    </div>
                                </div>
                            </>
                        )}
                    </div>

                    {/* Invite modal */}
                    {inviteOpen && (
                        <div style={{position:'fixed', inset:0, background:'rgba(5,9,16,0.62)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:100}} onClick={() => !inviteBusy && setInviteOpen(false)}>
                            <div style={{background:'var(--zone-2)', border:'1px solid var(--rule-2)', padding:'32px', width:'420px'}} onClick={e => e.stopPropagation()}>
                                <div className="font-display" style={{fontSize:'14px', letterSpacing:'0.1em', textTransform:'uppercase', color:'var(--gold)', marginBottom:'20px'}}>
                                    Invite Admin
                                </div>
                                <form onSubmit={invite}>
                                    <div style={{marginBottom:'14px'}}>
                                        <label style={{display:'block', fontSize:'11px', letterSpacing:'0.09em', textTransform:'uppercase', color:'var(--text-muted)', marginBottom:'6px'}}>Full Name</label>
                                        <input type="text" value={inviteForm.full_name} onChange={e => setInviteForm({...inviteForm, full_name: e.target.value})} disabled={inviteBusy} autoFocus style={{width:'100%', background:'var(--zone-3)', color:'var(--text)', border:'1px solid var(--rule-2)', padding:'10px 12px', fontSize:'14px'}} />
                                    </div>
                                    <div style={{marginBottom:'18px'}}>
                                        <label style={{display:'block', fontSize:'11px', letterSpacing:'0.09em', textTransform:'uppercase', color:'var(--text-muted)', marginBottom:'6px'}}>Email</label>
                                        <input type="email" value={inviteForm.email} onChange={e => setInviteForm({...inviteForm, email: e.target.value})} disabled={inviteBusy} style={{width:'100%', background:'var(--zone-3)', color:'var(--text)', border:'1px solid var(--rule-2)', padding:'10px 12px', fontSize:'14px'}} />
                                    </div>
                                    {inviteMsg && <div style={{color: inviteMsg.startsWith('Invitation') ? 'var(--green)' : 'var(--red)', fontSize:'13px', marginBottom:'12px'}}>{inviteMsg}</div>}
                                    <div style={{display:'flex', gap:'10px'}}>
                                        <button type="button" onClick={() => setInviteOpen(false)} disabled={inviteBusy} className="btn-gold" style={{flex:1, background:'transparent', color:'var(--text-muted)', border:'1px solid var(--rule-2)'}}>Cancel</button>
                                        <button type="submit" disabled={inviteBusy} className="btn-gold" style={{flex:1}}>{inviteBusy ? 'Sending…' : 'Send Invitation'}</button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    )}
                </div>
            );
        }

export { AdminPanel };
